// DictionaryUploadList.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Upload, FileText, Calendar, FolderOpen, Building, X } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const DictionaryUploadList = ({ onAddWork }) => {
  const [uploads, setUploads] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    dictionary_type: 'diagnostic',
    file: null,
    projects: [],
    customers: [],
  });
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const dictionaryTypes = [
    { value: 'diagnostic', label: 'Диагностика', icon: '🔍' },
    { value: 'repair', label: 'Ремонт', icon: '🔧' },
  ];

  useEffect(() => {
    fetchUploads();
    fetchProjects();
    fetchCustomers();
  }, []);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/dictionary-uploads/');
      setUploads(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки справочников');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/settings/projects/');
      setProjects(response.data.map(p => ({ value: p.id, label: p.name })));
    } catch (error) {
      toast.error('Ошибка загрузки проектов');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/settings/customers/');
      setCustomers(response.data.map(c => ({ value: c.id, label: c.name })));
    } catch (error) {
      toast.error('Ошибка загрузки заказчиков');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('name', formData.name);
    data.append('dictionary_type', formData.dictionary_type);
    if (formData.file) data.append('file', formData.file);
    formData.projects.forEach(project => data.append('projects_ids', project.value));
    formData.customers.forEach(customer => data.append('customers_ids', customer.value));

    try {
      let response;
      if (editingId) {
        response = await api.put(`/settings/dictionary-uploads/${editingId}/`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post('/settings/dictionary-uploads/', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      if (response.data?.error_log) {
        toast.error(`Справочник сохранён, но файл не импортирован: ${response.data.error_log}`);
      } else {
        toast.success(editingId ? 'Справочник обновлён' : 'Справочник создан');
      }
      closeModal();
      fetchUploads();
    } catch (error) {
      toast.error('Ошибка сохранения справочника');
      console.error('Submit error:', error.response?.data);
    }
  };

  const handleEdit = (upload) => {
    setFormData({
      name: upload.name,
      dictionary_type: upload.dictionary_type,
      file: null,
      projects: projects.filter(p => upload.projects.some(up => up.id === p.value)),
      customers: customers.filter(c => upload.customers.some(uc => uc.id === c.value)),
    });
    setEditingId(upload.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить справочник?')) {
      try {
        await api.delete(`/settings/dictionary-uploads/${id}/`);
        toast.success('Справочник удалён');
        fetchUploads();
      } catch (error) {
        toast.error('Ошибка удаления справочника');
      }
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0] });
  };

  const openAddModal = () => {
    setFormData({
      name: '',
      dictionary_type: 'diagnostic',
      file: null,
      projects: [],
      customers: [],
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      name: '',
      dictionary_type: 'diagnostic',
      file: null,
      projects: [],
      customers: [],
    });
    setEditingId(null);
  };

  const getDictionaryTypeInfo = (type) => {
    return dictionaryTypes.find(dt => dt.value === type) || dictionaryTypes[0];
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка справочников...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <FileText size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Загрузки справочников
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить справочник
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {uploads.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <FileText size={48} />
            </div>
            <h3>Нет справочников</h3>
            <p>Добавьте первый справочник для обработки данных</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название</th>
                <th>Тип</th>
                <th>Дата загрузки</th>
                <th>Проекты</th>
                <th>Заказчики</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map(upload => {
                const typeInfo = getDictionaryTypeInfo(upload.dictionary_type);
                return (
                  <tr key={upload.id} className={styles.clickableRow} onClick={() => handleEdit(upload)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FileText size={16} style={{ color: '#0f766e' }} />
                        <strong>{upload.name}</strong>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span>{typeInfo.icon}</span>
                        <span>{typeInfo.label}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} style={{ color: '#64748b' }} />
                        {new Date(upload.uploaded_at).toLocaleString('ru-RU')}
                      </div>
                    </td>
                    <td style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                      {upload.projects.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {upload.projects.map(p => (
                            <span
                              key={p.id}
                              style={{
                                background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                                color: '#1e40af',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                            >
                              {p.name}
                            </span>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                      {upload.customers.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {upload.customers.map(c => (
                            <span
                              key={c.id}
                              style={{
                                background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                                color: '#065f46',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                            >
                              {c.name}
                            </span>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td>
                      <div className={styles.tableActions}>
                      {upload.dictionary_type === 'repair' && onAddWork && (
                        <button
                          type="button"
                          className={styles.inlineAddButton}
                          onClick={(event) => { event.stopPropagation(); onAddWork(upload); }}
                          disabled={!upload.projects?.length}
                          title="Добавить работу в этот справочник"
                        >
                          <Plus size={15} /> Работа
                        </button>
                      )}
                      <button
                        className={styles.actionButton}
                        onClick={(event) => { event.stopPropagation(); handleDelete(upload.id); }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <FileText size={20} />
                {editingId ? 'Редактировать справочник' : 'Добавить справочник'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <FileText size={16} />
                    Название справочника
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Введите название справочника"
                    required
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <FileText size={16} />
                    Тип справочника
                  </label>
                  <div className={styles.select}>
                    <Select
                      options={dictionaryTypes}
                      value={dictionaryTypes.find(dt => dt.value === formData.dictionary_type)}
                      onChange={(selected) => setFormData({ ...formData, dictionary_type: selected.value })}
                      formatOptionLabel={(option) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{option.icon}</span>
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Upload size={16} />
                    Файл Excel
                  </label>
                  <div className={styles.fileInputWrapper}>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileChange}
                      id="dictionary-file-upload"
                    />
                    <button
                      type="button"
                      className={styles.uploadButton}
                      onClick={() => document.getElementById('dictionary-file-upload').click()}
                    >
                      <Upload size={16} />
                      Выбрать файл
                    </button>
                    {formData.file && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem',
                        color: '#065f46',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}>
                        <FileText size={16} />
                        {formData.file.name}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <FolderOpen size={16} />
                    Проекты
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={projects}
                      value={formData.projects}
                      onChange={(selected) => setFormData({ ...formData, projects: selected || [] })}
                      placeholder="Выберите проекты..."
                      isDisabled={!projects.length}
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    Заказчики
                  </label>
                  <div className={styles.select}>
                    <Select
                      isMulti
                      options={customers}
                      value={formData.customers}
                      onChange={(selected) => setFormData({ ...formData, customers: selected || [] })}
                      placeholder="Выберите заказчиков..."
                      isDisabled={!customers.length}
                    />
                  </div>
                </div>

                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить справочник' : 'Создать справочник'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DictionaryUploadList;
